/**
 * Animation Engine
 * Plays timeline animations using the Web Animations API
 */

import type {
  Timeline,
  AnimationTrack,
  Keyframe,
  AnimatableProperty,
  PropertyValue,
  PlaybackState,
  PlaybackController,
  Easing,
} from '@/types/animation';
import { easingToCSS, getTrackDuration, calculateTimelineDuration } from '@/types/animation';
import type { AudioLayer, Layer } from '@/types/layers';
import { isAudioLayer } from '@/types/layers';

// ============================================================================
// LAYER ELEMENT MAPPING
// ============================================================================

/**
 * Maps layer IDs to their DOM elements
 */
type LayerElementMap = Map<string, HTMLElement>;

/**
 * Get the DOM element for a layer by ID
 */
function getLayerElement(layerId: string): HTMLElement | null {
  return document.querySelector(`[data-layer-id="${layerId}"]`);
}

/**
 * Build a map of all layer elements
 */
function buildLayerElementMap(layerIds: string[]): LayerElementMap {
  const map: LayerElementMap = new Map();
  for (const id of layerIds) {
    const element = getLayerElement(id);
    if (element) {
      map.set(id, element);
    }
  }
  return map;
}

// ============================================================================
// KEYFRAME CONVERSION
// ============================================================================

/**
 * Convert our keyframe format to Web Animations API format
 */
function convertKeyframes(
  keyframes: Keyframe[],
  trackDuration: number,
  defaultEasing: Easing
): globalThis.Keyframe[] {
  if (keyframes.length === 0) return [];

  // Sort by time
  const sorted = [...keyframes].sort((a, b) => a.time - b.time);

  return sorted.map((kf) => {
    const webKeyframe: globalThis.Keyframe = {
      offset: trackDuration > 0 ? kf.time / trackDuration : 0,
      easing: easingToCSS(kf.easing ?? defaultEasing),
    };

    // Convert our properties to CSS properties
    for (const [prop, value] of Object.entries(kf.properties)) {
      const cssProperty = toCSSProperty(prop as AnimatableProperty, value);
      if (cssProperty) {
        Object.assign(webKeyframe, cssProperty);
      }
    }

    return webKeyframe;
  });
}

/**
 * Convert our property names to CSS property names
 */
function toCSSProperty(
  property: AnimatableProperty,
  value: PropertyValue
): Record<string, string | number> | null {
  switch (property) {
    case 'x':
      return { transform: `translateX(${value}px)` };
    case 'y':
      return { transform: `translateY(${value}px)` };
    case 'scale':
      return { transform: `scale(${value})` };
    case 'scaleX':
      return { transform: `scaleX(${value})` };
    case 'scaleY':
      return { transform: `scaleY(${value})` };
    case 'rotation':
      return { transform: `rotate(${value}deg)` };
    case 'skewX':
      return { transform: `skewX(${value}deg)` };
    case 'skewY':
      return { transform: `skewY(${value}deg)` };
    case 'opacity':
      return { opacity: Number(value) };
    case 'visible':
      return { visibility: value ? 'visible' : 'hidden' };
    case 'fill':
    case 'backgroundColor':
      return { backgroundColor: String(value) };
    case 'stroke':
      return { borderColor: String(value) };
    case 'color':
      return { color: String(value) };
    case 'fontSize':
      return { fontSize: `${value}px` };
    case 'letterSpacing':
      return { letterSpacing: `${value}px` };
    case 'borderRadius':
      return { borderRadius: `${value}px` };
    case 'strokeWidth':
      return { borderWidth: `${value}px` };
    case 'width':
      return { width: `${value}px` };
    case 'height':
      return { height: `${value}px` };
    default:
      return null;
  }
}

/**
 * Handle composite transforms (when multiple transform properties are animated)
 */
function buildCompositeKeyframes(
  keyframes: Keyframe[],
  trackDuration: number,
  defaultEasing: Easing
): globalThis.Keyframe[] {
  if (keyframes.length === 0) return [];

  const sorted = [...keyframes].sort((a, b) => a.time - b.time);

  return sorted.map((kf) => {
    const webKeyframe: globalThis.Keyframe = {
      offset: trackDuration > 0 ? kf.time / trackDuration : 0,
      easing: easingToCSS(kf.easing ?? defaultEasing),
    };

    // Build composite transform
    const transforms: string[] = [];
    const otherProps: Record<string, string | number> = {};

    for (const [prop, value] of Object.entries(kf.properties)) {
      switch (prop as AnimatableProperty) {
        case 'x':
          transforms.push(`translateX(${value}px)`);
          break;
        case 'y':
          transforms.push(`translateY(${value}px)`);
          break;
        case 'scale':
          transforms.push(`scale(${value})`);
          break;
        case 'scaleX':
          transforms.push(`scaleX(${value})`);
          break;
        case 'scaleY':
          transforms.push(`scaleY(${value})`);
          break;
        case 'rotation':
          transforms.push(`rotate(${value}deg)`);
          break;
        case 'skewX':
          transforms.push(`skewX(${value}deg)`);
          break;
        case 'skewY':
          transforms.push(`skewY(${value}deg)`);
          break;
        case 'opacity':
          otherProps.opacity = Number(value);
          break;
        case 'visible':
          otherProps.visibility = value ? 'visible' : 'hidden';
          break;
        case 'fill':
        case 'backgroundColor':
          otherProps.backgroundColor = String(value);
          break;
        case 'color':
          otherProps.color = String(value);
          break;
        case 'fontSize':
          otherProps.fontSize = `${value}px`;
          break;
        case 'width':
          otherProps.width = `${value}px`;
          break;
        case 'height':
          otherProps.height = `${value}px`;
          break;
        case 'borderRadius':
          otherProps.borderRadius = `${value}px`;
          break;
      }
    }

    if (transforms.length > 0) {
      webKeyframe.transform = transforms.join(' ');
    }

    return { ...webKeyframe, ...otherProps };
  });
}

// ============================================================================
// ANIMATION ENGINE
// ============================================================================

/**
 * Active animation instance
 */
interface ActiveAnimation {
  track: AnimationTrack;
  element: HTMLElement;
  animation: Animation;
}

/**
 * Active audio instance for playback sync
 */
interface ActiveAudio {
  layer: AudioLayer;
  element: HTMLAudioElement;
  gainNode?: GainNode;
}

/**
 * Animation Engine
 * Manages playback of a timeline
 */
export class AnimationEngine implements PlaybackController {
  private timeline: Timeline;
  private layerElements: LayerElementMap = new Map();
  private activeAnimations: ActiveAnimation[] = [];
  private activeAudio: ActiveAudio[] = [];
  private audioContext: AudioContext | null = null;
  private masterPlayback: Animation | null = null;

  state: PlaybackState = 'idle';
  currentTime = 0;
  duration = 0;
  playbackRate = 1;

  private stateChangeCallbacks: Set<(state: PlaybackState) => void> = new Set();
  private timeUpdateCallbacks: Set<(time: number) => void> = new Set();

  constructor(timeline: Timeline) {
    this.timeline = timeline;
    this.duration = timeline.duration ?? calculateTimelineDuration(timeline.tracks);
    this.playbackRate = timeline.playbackRate;
  }

  /**
   * Initialize the engine (find DOM elements)
   */
  initialize(): boolean {
    const layerIds = this.timeline.tracks.map(t => t.layerId);
    this.layerElements = buildLayerElementMap(layerIds);

    // Check if all layers were found
    const missingLayers = layerIds.filter(id => !this.layerElements.has(id));
    if (missingLayers.length > 0) {
      console.warn('Animation engine: Missing layer elements:', missingLayers);
    }

    return missingLayers.length === 0;
  }

  /**
   * Initialize audio layers for playback
   */
  initializeAudio(layers: Layer[]): void {
    // Dispose any existing audio
    this.disposeAudio();

    // Filter to audio layers only
    const audioLayers = layers.filter(isAudioLayer);
    if (audioLayers.length === 0) return;

    // Create audio context if needed
    if (!this.audioContext) {
      this.audioContext = new AudioContext();
    }

    // Create audio elements for each layer
    for (const layer of audioLayers) {
      if (!layer.visible) continue;

      const audio = new Audio();
      audio.src = layer.content.src;
      audio.loop = layer.content.loop ?? false;
      audio.volume = layer.content.volume ?? 1;
      audio.preload = 'auto';

      // Create gain node for volume control with fades
      let gainNode: GainNode | undefined;
      if (this.audioContext && (layer.content.fadeIn || layer.content.fadeOut)) {
        const source = this.audioContext.createMediaElementSource(audio);
        gainNode = this.audioContext.createGain();
        source.connect(gainNode);
        gainNode.connect(this.audioContext.destination);
      }

      this.activeAudio.push({
        layer,
        element: audio,
        gainNode,
      });
    }
  }

  /**
   * Dispose audio elements
   */
  private disposeAudio(): void {
    for (const { element } of this.activeAudio) {
      element.pause();
      element.src = '';
    }
    this.activeAudio = [];
  }

  /**
   * Apply fade effects to audio
   */
  private applyAudioFades(activeAudio: ActiveAudio, currentTimeMs: number): void {
    const { layer, gainNode, element } = activeAudio;
    if (!gainNode || !this.audioContext) return;

    const fadeIn = layer.content.fadeIn ?? 0;
    const fadeOut = layer.content.fadeOut ?? 0;
    const duration = (layer.content.duration ?? 0) * 1000;
    const volume = layer.content.volume ?? 1;

    // Calculate current gain
    let gain = volume;

    // Apply fade in
    if (fadeIn > 0 && currentTimeMs < fadeIn) {
      gain = (currentTimeMs / fadeIn) * volume;
    }

    // Apply fade out
    if (fadeOut > 0 && duration > 0) {
      const fadeOutStart = duration - fadeOut;
      if (currentTimeMs > fadeOutStart) {
        gain = ((duration - currentTimeMs) / fadeOut) * volume;
      }
    }

    gainNode.gain.setValueAtTime(Math.max(0, Math.min(1, gain)), this.audioContext.currentTime);
  }

  /**
   * Create animations for all tracks
   */
  private createAnimations(): void {
    this.disposeAnimations();

    for (const track of this.timeline.tracks) {
      if (!track.enabled) continue;

      const element = this.layerElements.get(track.layerId);
      if (!element) continue;

      const trackDuration = getTrackDuration(track);
      if (trackDuration === 0) continue;

      // Build keyframes
      const keyframes = buildCompositeKeyframes(
        track.keyframes,
        trackDuration,
        track.easing
      );

      if (keyframes.length < 2) continue;

      // Create animation options
      const options: KeyframeAnimationOptions = {
        duration: trackDuration,
        delay: track.startTime,
        fill: track.fillMode ?? 'forwards',
        iterations: track.repeat?.count === 'infinite' ? Infinity : (track.repeat?.count ?? 1),
        direction: track.repeat?.direction ?? 'normal',
        easing: easingToCSS(track.easing),
      };

      // Create the animation
      const animation = element.animate(keyframes, options);
      animation.pause(); // Start paused

      this.activeAnimations.push({
        track,
        element,
        animation,
      });
    }
  }

  /**
   * Dispose all active animations
   */
  private disposeAnimations(): void {
    for (const { animation } of this.activeAnimations) {
      animation.cancel();
    }
    this.activeAnimations = [];
  }

  /**
   * Start playback
   */
  play(): void {
    if (this.state === 'idle' || this.activeAnimations.length === 0) {
      this.createAnimations();
    }

    for (const { animation } of this.activeAnimations) {
      animation.play();
    }

    // Resume audio context if suspended
    if (this.audioContext?.state === 'suspended') {
      this.audioContext.resume();
    }

    // Play all audio elements
    for (const activeAudio of this.activeAudio) {
      const { layer, element } = activeAudio;
      const startTime = layer.content.startTime ?? 0;

      // Only play if we're past the layer's start point
      if (this.currentTime >= startTime * 1000) {
        element.currentTime = (this.currentTime / 1000) - startTime;
        element.play().catch(() => {
          // Audio play can fail if not triggered by user interaction
        });
      }
    }

    this.state = 'playing';
    this.notifyStateChange();
    this.startTimeTracking();
  }

  /**
   * Pause playback
   */
  pause(): void {
    for (const { animation } of this.activeAnimations) {
      animation.pause();
    }

    // Pause all audio elements
    for (const { element } of this.activeAudio) {
      element.pause();
    }

    this.state = 'paused';
    this.notifyStateChange();
    this.stopTimeTracking();
  }

  /**
   * Stop playback and reset
   */
  stop(): void {
    this.disposeAnimations();

    // Stop and reset all audio elements
    for (const { element } of this.activeAudio) {
      element.pause();
      element.currentTime = 0;
    }

    this.currentTime = 0;
    this.state = 'idle';
    this.notifyStateChange();
    this.notifyTimeUpdate();
    this.stopTimeTracking();
  }

  /**
   * Seek to a specific time
   */
  seek(time: number): void {
    this.currentTime = Math.max(0, Math.min(time, this.duration));

    for (const { animation, track } of this.activeAnimations) {
      // Adjust time relative to track start
      const trackTime = this.currentTime - track.startTime;
      if (trackTime >= 0) {
        animation.currentTime = trackTime;
      }
    }

    // Seek all audio elements
    for (const activeAudio of this.activeAudio) {
      const { layer, element } = activeAudio;
      const startTime = (layer.content.startTime ?? 0) * 1000;
      const endTime = layer.content.endTime
        ? layer.content.endTime * 1000
        : (layer.content.duration ?? Infinity) * 1000;

      // Calculate audio time relative to layer's trim points
      const audioTime = (this.currentTime - startTime) / 1000;

      if (this.currentTime >= startTime && this.currentTime < startTime + endTime) {
        element.currentTime = Math.max(0, audioTime);

        // Apply fades at seek position
        this.applyAudioFades(activeAudio, this.currentTime);
      } else {
        element.currentTime = 0;
      }
    }

    this.notifyTimeUpdate();
  }

  /**
   * Set playback rate
   */
  setPlaybackRate(rate: number): void {
    this.playbackRate = rate;

    for (const { animation } of this.activeAnimations) {
      animation.playbackRate = rate;
    }
  }

  /**
   * Subscribe to state changes
   */
  onStateChange(callback: (state: PlaybackState) => void): () => void {
    this.stateChangeCallbacks.add(callback);
    return () => this.stateChangeCallbacks.delete(callback);
  }

  /**
   * Subscribe to time updates
   */
  onTimeUpdate(callback: (time: number) => void): () => void {
    this.timeUpdateCallbacks.add(callback);
    return () => this.timeUpdateCallbacks.delete(callback);
  }

  private notifyStateChange(): void {
    for (const callback of this.stateChangeCallbacks) {
      callback(this.state);
    }
  }

  private notifyTimeUpdate(): void {
    for (const callback of this.timeUpdateCallbacks) {
      callback(this.currentTime);
    }
  }

  private animationFrameId: number | null = null;

  private startTimeTracking(): void {
    const update = () => {
      if (this.state !== 'playing') return;

      // Get current time from the first animation
      if (this.activeAnimations.length > 0) {
        const firstAnim = this.activeAnimations[0];
        const animTime = firstAnim.animation.currentTime;
        if (typeof animTime === 'number') {
          this.currentTime = animTime + firstAnim.track.startTime;
        }
      }

      this.notifyTimeUpdate();

      // Check if finished
      const allFinished = this.activeAnimations.every(
        ({ animation }) => animation.playState === 'finished'
      );

      if (allFinished) {
        if (this.timeline.loop) {
          // Restart
          this.seek(0);
          this.play();
        } else {
          this.state = 'finished';
          this.notifyStateChange();
          this.stopTimeTracking();
        }
      } else {
        this.animationFrameId = requestAnimationFrame(update);
      }
    };

    this.animationFrameId = requestAnimationFrame(update);
  }

  private stopTimeTracking(): void {
    if (this.animationFrameId !== null) {
      cancelAnimationFrame(this.animationFrameId);
      this.animationFrameId = null;
    }
  }

  /**
   * Set volume for a specific audio layer
   */
  setAudioVolume(layerId: string, volume: number): void {
    const activeAudio = this.activeAudio.find(a => a.layer.id === layerId);
    if (activeAudio) {
      activeAudio.element.volume = Math.max(0, Math.min(1, volume));
    }
  }

  /**
   * Mute/unmute a specific audio layer
   */
  setAudioMuted(layerId: string, muted: boolean): void {
    const activeAudio = this.activeAudio.find(a => a.layer.id === layerId);
    if (activeAudio) {
      activeAudio.element.muted = muted;
    }
  }

  /**
   * Get all active audio layers
   */
  getActiveAudioLayers(): AudioLayer[] {
    return this.activeAudio.map(a => a.layer);
  }

  /**
   * Dispose the engine
   */
  dispose(): void {
    this.stop();
    this.disposeAudio();
    if (this.audioContext) {
      this.audioContext.close();
      this.audioContext = null;
    }
    this.stateChangeCallbacks.clear();
    this.timeUpdateCallbacks.clear();
    this.layerElements.clear();
  }
}

/**
 * Create an animation engine for a timeline
 */
export function createAnimationEngine(timeline: Timeline): AnimationEngine {
  return new AnimationEngine(timeline);
}
